// Разбор телеметрии живых забегов. Раздел 2 контракта.
//
// Формат текстовый и живёт в общем логе Unity вперемешку со всем прочим,
// поэтому разбор идёт по метке `spusk.run v=`, а не по строкам файла.

import type { TelemetryRecord, TelemetryReport } from './types';

const MARKER = 'spusk.run v=';
/** Разрыв, после которого начинается новая сессия. По умолчанию 30 минут. */
export const DEFAULT_SESSION_GAP_MINUTES = 30;

function parseFields(line: string): Map<string, string> {
  const fields = new Map<string, string>();
  for (const token of line.trim().split(/\s+/)) {
    const eq = token.indexOf('=');
    if (eq <= 0) continue;
    fields.set(token.slice(0, eq), token.slice(eq + 1));
  }
  return fields;
}

const int = (fields: Map<string, string>, key: string, fallback = 0): number => {
  const raw = fields.get(key);
  if (raw == null) return fallback;
  const value = Number.parseInt(raw, 10);
  return Number.isFinite(value) ? value : fallback;
};

const optionalInt = (fields: Map<string, string>, key: string): number | null => {
  if (!fields.has(key)) return null;
  return int(fields, key);
};

/**
 * Разбирает любой текст, в котором встречается метка.
 *
 * Схемы v=1/v=2/v=3. Отсутствующее поле превращается в null, а не в ноль:
 * иначе старые записи молча сообщали бы то, чего сток тогда не измерял.
 */
export function parseTelemetry(
  text: string,
  gapMinutes = DEFAULT_SESSION_GAP_MINUTES,
): Omit<TelemetryReport, 'origin'> {
  const malformed: string[] = [];
  const parsed: TelemetryRecord[] = [];
  const seen = new Set<string>();
  let duplicatesRemoved = 0;

  for (const rawLine of text.split(/\r?\n/)) {
    const at = rawLine.indexOf(MARKER);
    if (at < 0) continue;
    const line = rawLine.slice(at).trim();

    // Сток пишет каждую запись дважды — в свой файл и в общий лог Unity.
    // Точные дубликаты снимаются, и снятое показывается числом.
    if (seen.has(line)) {
      duplicatesRemoved++;
      continue;
    }
    seen.add(line);

    const fields = parseFields(line);
    const v = Number.parseInt(line.slice(MARKER.length), 10);
    const seed = fields.get('seed');
    const outcome = fields.get('outcome');
    const atStamp = fields.get('at');
    if (!seed || !outcome || !atStamp || !Number.isFinite(v)) {
      malformed.push(line);
      continue;
    }

    parsed.push({
      version: v,
      seed,
      outcome,
      cause: fields.get('cause') ?? 'Unknown',
      killer: int(fields, 'killer', -1),
      tick: int(fields, 'tick'),
      sec: Number.parseFloat(fields.get('sec') ?? '0') || 0,
      kills: int(fields, 'kills'),
      loot: int(fields, 'loot'),
      cheats: fields.has('cheats') ? int(fields, 'cheats') : null,
      act: fields.get('act') ?? null,
      level: fields.get('level') ?? null,
      hp: optionalInt(fields, 'hp'),
      hpmax: optionalInt(fields, 'hpmax'),
      ammo: optionalInt(fields, 'ammo'),
      medkits: optionalInt(fields, 'medkits'),
      batteries: optionalInt(fields, 'batteries'),
      talons: optionalInt(fields, 'talons'),
      weightg: optionalInt(fields, 'weightg'),
      shots: optionalInt(fields, 'shots'),
      hits: optionalInt(fields, 'hits'),
      dry: optionalInt(fields, 'dry'),
      melee: optionalInt(fields, 'melee'),
      meleehits: optionalInt(fields, 'meleehits'),
      dmg: optionalInt(fields, 'dmg'),
      heal: optionalInt(fields, 'heal'),
      meduse: optionalInt(fields, 'meduse'),
      threat: optionalInt(fields, 'threat'),
      warden: optionalInt(fields, 'warden'),
      wardenticks: optionalInt(fields, 'wardenticks'),
      at: atStamp,
      session: 0,
      raw: line,
    });
  }

  parsed.sort((a, b) => Date.parse(a.at) - Date.parse(b.at));

  let session = 0;
  let previous = Number.NaN;
  const gapMs = gapMinutes * 60_000;
  for (const record of parsed) {
    const stamp = Date.parse(record.at);
    if (!Number.isFinite(previous) || stamp - previous > gapMs) session++;
    record.session = session;
    previous = stamp;
  }

  return {
    records: parsed,
    duplicatesRemoved,
    malformed,
    inProgress: parsed.filter((r) => r.outcome === 'InProgress').length,
    sessions: session,
  };
}

export interface TelemetrySummary {
  total: number;
  /** Записи с cheats=1 исключены из баланса, но остаются видимыми. */
  cheated: TelemetryRecord[];
  clean: TelemetryRecord[];
  outcomes: Array<{ outcome: string; count: number; share: number }>;
  causes: Array<{ cause: string; count: number; share: number }>;
  killers: Array<{ killer: number; count: number }>;
  sessions: Array<{
    index: number;
    from: string;
    to: string;
    runs: number;
    died: number;
    evacuated: number;
    medianSec: number;
  }>;
  survivalShare: number;
  medianSec: number;
  unknownCheats: number;
  v3: TelemetryRecord[];
  medianAccuracy: number | null;
  medianAmmo: number | null;
  medianHealthShare: number | null;
  medianThreat: number | null;
}

function median(values: number[]): number {
  if (values.length === 0) return Number.NaN;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor((sorted.length - 1) / 2)] as number;
}

export function summarizeTelemetry(records: readonly TelemetryRecord[]): TelemetrySummary {
  const cheated = records.filter((r) => r.cheats === 1);
  const clean = records.filter((r) => r.cheats !== 1);
  const n = Math.max(1, clean.length);
  const v3 = clean.filter((r) => r.version >= 3);
  const medianKnown = (values: Array<number | null>): number | null => {
    const known = values.filter((value): value is number => value !== null && value >= 0);
    return known.length === 0 ? null : median(known);
  };
  const accuracies = v3
    .filter((r) => r.shots !== null && r.hits !== null && r.shots > 0)
    .map((r) => (r.hits as number) / (r.shots as number));

  const tally = <T extends string | number>(pick: (r: TelemetryRecord) => T) => {
    const counts = new Map<T, number>();
    for (const record of clean) counts.set(pick(record), (counts.get(pick(record)) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  };

  const sessionIds = [...new Set(clean.map((r) => r.session))].sort((a, b) => a - b);
  const sessions = sessionIds.map((index) => {
    const inSession = clean.filter((r) => r.session === index);
    return {
      index,
      from: inSession[0]?.at ?? '',
      to: inSession[inSession.length - 1]?.at ?? '',
      runs: inSession.length,
      died: inSession.filter((r) => r.outcome === 'Died').length,
      evacuated: inSession.filter((r) => r.outcome === 'Evacuated').length,
      medianSec: median(inSession.map((r) => r.sec)),
    };
  });

  return {
    total: records.length,
    cheated,
    clean,
    outcomes: tally((r) => r.outcome).map(([outcome, count]) => ({ outcome, count, share: count / n })),
    causes: tally((r) => r.cause).map(([cause, count]) => ({ cause, count, share: count / n })),
    killers: tally((r) => r.killer)
      .filter(([killer]) => killer >= 0)
      .map(([killer, count]) => ({ killer, count })),
    sessions,
    survivalShare: clean.filter((r) => r.outcome === 'Evacuated').length / n,
    medianSec: median(clean.map((r) => r.sec)),
    unknownCheats: clean.filter((r) => r.cheats === null).length,
    v3,
    medianAccuracy: accuracies.length === 0 ? null : median(accuracies),
    medianAmmo: medianKnown(v3.map((r) => r.ammo)),
    medianHealthShare: medianKnown(
      v3.map((r) => (r.hp !== null && r.hpmax !== null && r.hp >= 0 && r.hpmax > 0 ? r.hp / r.hpmax : null)),
    ),
    medianThreat: medianKnown(v3.map((r) => r.threat)),
  };
}
